/**
 * Crime Ops – Tab "LABOR" (Phase 3.4 Equipment-System)
 *
 * 3 Bereiche:
 *   EQUIPMENT  – 6 Slots, installieren / ausbauen (PropertySlot-System)
 *   KÖCHE      – aktive Kochvorgänge mit Progress + Einsammeln
 *   REZEPTE    – Katalog mit STARTEN-Button
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';
import { formatHideoutName } from '../../utils/hideout-format.js';
import { filterHideoutsByExpansion, renderHideoutSelectorTabs, renderHideoutSelectorEmpty } from '../../utils/hideout-selector.js';
import { tryOfferEmergencyGarage } from '../EmergencyGarageOfferModal.js';

// ─── Typen ────────────────────────────────────────────────────────────────────

interface LaborPropertyInfo {
  id:            string;
  label:         string;
  subType:       string;
  tier:          string;
  batches:       number;
  maxBatches:    number;
  glovesCharges: number;
  stripsCharges: number;
}

interface LabPropertySlot {
  id:              string;
  slotType:        string;
  slotIndex:       number;
  isUnlocked:      boolean;
  stoveItem:       string | null;
  glassItem:       string | null;
  safetyItem:      string | null;
  distillItem:     string | null;
  chemiItem:       string | null;
  capsuleItem:     string | null;
  glassItemBroken: boolean;
}

interface LabInventoryItem {
  itemKey:  string;
  quantity: number;
}

interface LabHideout {
  id:             string;
  subType:        string;
  customName:     string | null;
  nickname:       string | null;
  expansionType:  string | null;
  expansionLevel: number;
  propertySlots:  LabPropertySlot[];
  inventory:      LabInventoryItem[];
  /** NT-ORG-06 — gesetzt, wenn das Labor ein Org-Gebäude ist. */
  orgBuilding?:   { orgId: string } | null;
}

interface RecipeCatalogEntry {
  key:              string;
  label:            string;
  description:      string;
  cookTimeMinutes:  number;
  baseOutputQty:    number;
  outputValueCents: string;
  outputCurrency:   string;
  risk:             string;
  requiredTier:     string;
  lore:             string;
  locked:              boolean;
  lockReason:          string | null;
  ingredients:         Record<string, number>;
  requiredSlots:       string[];
  ingredientChoices?:  { choiceGroup: string[]; quantity: number }[];
  outputInventoryType?: string;
}

/** Verfügbarkeit eines Rezepts für EINEN Kochdurchgang — vom Backend berechnet (NT-UI-LABOR-BESTAND-01). */
interface RecipeAvailability {
  ingredients: Record<string, boolean>;
  slots:       Record<string, boolean>;
}

interface CookBatchInfo {
  id:              string;
  recipeKey:       string;
  recipeLabel:     string;
  propertyId:      string;
  propertyLabel:   string;
  startedAt:       string;
  completesAt:     string;
  status:          string;
  progressPercent: number;
}

// ─── Mappings ─────────────────────────────────────────────────────────────────

interface SlotFieldDef {
  field:      string;
  label:      string;
  validItems: string[];
  minLevel:   number;
}

const SLOT_FIELD_CONFIG: SlotFieldDef[] = [
  { field: 'stoveItem',   label: 'HERD',         validItems: ['lab_stove',          'herd_brenner'],      minLevel: 1 },
  { field: 'glassItem',   label: 'GLASKOLBEN',   validItems: ['lab_glassware',       'glaskolben_set'],   minLevel: 1 },
  { field: 'safetyItem',  label: 'SCHUTZKIT',    validItems: ['lab_protection',      'schutzausruestung'], minLevel: 1 },
  { field: 'distillItem', label: 'DESTILLATION', validItems: ['lab_distiller',       'destillieraufsatz'], minLevel: 1 },
  { field: 'chemiItem',   label: 'CHEMIE-SET',   validItems: ['lab_chemistry_set',   'chemi_set'],        minLevel: 1 },
  { field: 'capsuleItem', label: 'KAPSEL',        validItems: ['lab_capsule_machine', 'kapselmaschine'],   minLevel: 1 },
];

// Slot-Schlüssel (Backend) → Anzeigename (Deutsch)
const SLOT_KEY_LABEL: Record<string, string> = {
  stove:         'HERD',
  glassware:     'GLASKOLBEN',
  protection:    'SCHUTZKIT',
  distiller:     'DESTILLATION',
  chemistry_set: 'CHEMIE-SET',
  capsule_machine: 'KAPSEL',
};

const INGREDIENT_LABELS: Record<string, string> = {
  gaerungsset:               'Gärungsset',
  zucker:                    'Zucker',
  malz:                      'Braumalz',
  hopfen:                    'Hopfen',
  staerke:                   'Maisstärke',
  kapselhuelsen:             'Kapselhülsen',
  duftstoffe:                'Duftstoff-Mix',
  koffein_extrakt:           'Koffein-Extrakt',
  taurin:                    'Taurin',
  ethanol:                   'Ethanol',
  isopropanol:               'Isopropanol',
  aktivkohle:                'Aktivkohle',
  natriumcarbonat:           'Natriumcarbonat',
  aceton:                    'Aceton',
  plastikformen:             'Plastikformen',
  aromaole:                  'Aromaöle',
  energydrink_basis:         'Energydrink-Basis',
  pseudoephedrin:            'Pseudoephedrin',
  salzsaeure:                'Salzsäure',
  methylamin:                'Methylamin',
  phosphorpentachlorid:      'Phosphorpentachlorid',
  lithiumstreifen:           'Lithiumstreifen',
  destillieraufsatz:         'Destillieraufsatz',
  katalysator_compound:      'Katalysator-Compound',
  synthetischer_precursor_x: 'Synthetischer Precursor X',
  // Pulver-Linie Zutaten
  backpulver:                'Backpulver',
  aetznatron:                'Ätznatron',
  bindemittel_konzentrat:    'Bindemittel-Konzentrat',
  // Pulver-Linie Outputs (als wählbare Zutaten für Kristallin/Alchemist)
  strassenpulver:            'Straßenpulver',
  laborpulver:               'Laborpulver',
  reinsubstanz:              'Reinsubstanz',
  // Darknet-Kaufzutaten (Heldentrank, NT-LAB-TRANK-01)
  leuchtserum:               'Leuchtserum',
  adrenalin_konzentrat:      'Adrenalin-Konzentrat',
};

const RISK_CLASS: Record<string, string> = {
  GERING:    'lt-val-green',
  MITTEL:    'lt-val-gold',
  HOCH:      'lt-val-orange',
  SEHR_HOCH: 'lt-val-red',
  EXTREM:    'lt-val-red',
};

/** Batch-Größe je Pulver-Sorte (= baseOutputQty aus der Rezeptdefinition). */
const PULVER_BATCH_SIZE: Record<string, number> = {
  strassenpulver: 25,
  laborpulver:    10,
  reinsubstanz:    5,
};

// ─── State ────────────────────────────────────────────────────────────────────

let isMounted          = false;
let labHideouts:         LabHideout[] = [];         // Auswahl-Quelle (aus /hideouts, gefiltert auf LABOR)
let labProps:            LaborPropertyInfo[] = [];  // Tier/Batches/Ladungen je Labor (aus /labor-v2/status)
let recipes:             RecipeCatalogEntry[] = [];
/** propertyId → recipeKey → Verfügbarkeit (aus /labor-v2/status, NT-UI-LABOR-BESTAND-01). */
let recipeAvailability:  Record<string, Record<string, RecipeAvailability>> = {};
let selectedPropId       = '';
let labHideout:          LabHideout | null = null;
let activeCooks:         CookBatchInfo[] = [];
let readyCooks:          CookBatchInfo[] = [];
let isLoading            = true;
let loadError:           string | null = null;
let countdownTimer:      ReturnType<typeof setInterval> | null = null;
let reloadPromise:       Promise<void> | null = null;
let actionInFlight       = false;
let equipmentCollapsed   = true;
let sectionLegalOpen     = false;
let sectionBasisOpen     = false;
let sectionIllegalOpen   = false;
let clickHandler:        ((e: Event) => void) | null = null;
let changeHandler:       ((e: Event) => void) | null = null;
/** Gewählte Pulver-Sorte je Rezept (recipeKey → choiceGroup-Index → gewählter itemKey). */
const pulverChoices      = new Map<string, string>();

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function fmtMins(m: number): string {
  if (m < 60) return `${String(m)} Min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${String(h)}h` : `${String(h)}h ${String(r)}min`;
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'FERTIG';
  const s = Math.floor(ms / 1_000);
  const h = Math.floor(s / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h)}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m)}:${String(sec).padStart(2, '0')}`;
}

/**
 * Farbcodiertes Bestand-Badge für Pulver-Sorten.
 * Zeigt Batch-Anzahl + Gesamt-Menge: z. B. "2x [50]".
 * Rot = 0, Gold = weniger als 1 Batch, Grün = ≥ 1 Batch.
 */
function pulverBadge(itemKey: string): string {
  const qty       = labHideout?.inventory.find((i) => i.itemKey === itemKey)?.quantity ?? 0;
  const batchSize = PULVER_BATCH_SIZE[itemKey] ?? 1;
  const batches   = Math.floor(qty / batchSize);
  const cssVar    = qty === 0 ? '--accent-red' : batches < 1 ? '--accent-gold' : '--accent-green';
  return `<span class="lt-pulver-badge" style="color:var(${cssVar});border-color:var(${cssVar});">${String(batches)}x [${String(qty)}]</span>`;
}


function ingredientLabel(key: string): string {
  return INGREDIENT_LABELS[key] ?? key;
}

/** Zutaten auf Lager, die von mindestens einem Rezept fest benötigt werden (keine Pulver-Wahl-Optionen — die zeigen ihren Bestand bereits inline über pulverBadge()). */
function getOwnedIngredients(): { key: string; qty: number }[] {
  if (!labHideout) return [];
  const neededKeys = new Set<string>();
  for (const r of recipes) {
    for (const k of Object.keys(r.ingredients)) neededKeys.add(k);
  }
  return labHideout.inventory
    .filter((i) => neededKeys.has(i.itemKey) && i.quantity > 0)
    .map((i) => ({ key: i.itemKey, qty: i.quantity }))
    .sort((a, b) => ingredientLabel(a.key).localeCompare(ingredientLabel(b.key), 'de'));
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

function addStyles(): void {
  const ID = 'lt-styles';
  let el = document.getElementById(ID);
  if (!el) { el = document.createElement('style'); el.id = ID; document.head.appendChild(el); }
  el.textContent = `
    #lt-root { font-family: var(--font-mono); font-size: var(--font-size-sm); color: var(--text-primary); }
    .lt-section { margin-bottom: var(--space-4); }
    .lt-label { font-size: var(--font-size-xs); letter-spacing: 0.1em; color: var(--text-muted); text-transform: uppercase;
                margin: 0 0 var(--space-2) 0; padding-bottom: 4px; border-bottom: 1px solid var(--border); }
    .lt-empty { font-size: var(--font-size-xs); color: var(--text-muted); margin: 0; }
    .lt-select { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary);
                  font-family: var(--font-mono); font-size: var(--font-size-xs); padding: 4px 8px;
                  border-radius: var(--radius); width: 100%; max-width: 340px; }
    /* Equipment */
    .lt-equip-header { display: flex; align-items: center; gap: var(--space-2);
                       padding-bottom: 4px; border-bottom: 1px solid var(--border); margin-bottom: var(--space-2); }
    .lt-equip-title { font-size: var(--font-size-xs); letter-spacing: 0.1em; color: var(--text-muted); text-transform: uppercase; }
    .lt-equip-toggle { background: none; border: 1px solid var(--border); color: var(--text-muted);
                       font-family: var(--font-mono); font-size: 10px; padding: 1px 6px;
                       border-radius: var(--radius); cursor: pointer; line-height: 1.6; }
    .lt-equip-toggle:hover { border-color: var(--text-muted); color: var(--text-primary); }
    .lt-slot-row { display: grid; grid-template-columns: 100px 1fr auto; align-items: center;
                   gap: var(--space-2); padding: var(--space-1) 0; border-bottom: 1px solid var(--border); }
    .lt-slot-row:last-child { border-bottom: none; }
    .lt-slot-name { font-size: var(--font-size-xs); color: var(--text-muted); text-transform: uppercase; }
    .lt-wear { font-size: 9px; color: var(--text-muted); margin-left: 6px; vertical-align: middle; white-space: nowrap; }
    /* Cooks */
    .lt-cook-row { padding: var(--space-2) 0; border-bottom: 1px solid var(--border); }
    .lt-cook-row:last-child { border-bottom: none; }
    .lt-cook-meta { display: flex; align-items: baseline; gap: var(--space-2); margin-bottom: var(--space-1); flex-wrap: wrap; }
    .lt-cook-name { font-size: var(--font-size-sm); color: var(--text-primary); }
    .lt-cook-lab  { font-size: var(--font-size-xs); color: var(--text-muted); }
    .lt-cook-time { font-size: var(--font-size-xs); color: var(--accent-gold); margin-left: auto; }
    .lt-progress  { height: 4px; background: var(--bg-elevated); border-radius: 2px; margin-bottom: var(--space-1); }
    .lt-progress-fill { height: 100%; background: var(--accent-green); border-radius: 2px; transition: width 1s linear; }
    .lt-cook-ready { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; padding: var(--space-1) 0; }
    /* Recipe Sections (collapsible) */
    .lt-rsec-header { display: flex; align-items: center; justify-content: space-between;
                      padding: var(--space-2) 0; border-bottom: 2px solid var(--border);
                      margin-bottom: var(--space-2); cursor: pointer; user-select: none; }
    .lt-rsec-header:hover .lt-rsec-title { opacity: 0.85; }
    .lt-rsec-title { font-size: var(--font-size-lg); letter-spacing: 0.12em; font-weight: bold;
                     text-transform: uppercase; }
    .lt-rsec-title--legal   { color: var(--accent-gold); }
    .lt-rsec-title--basis   { color: var(--accent-blue); }
    .lt-rsec-title--illegal { color: var(--accent-red); }
    .lt-rsec-chevron { font-size: var(--font-size-xs); color: var(--text-muted); }
    .lt-rsec-body { margin-bottom: var(--space-4); }
    /* Recipes */
    .lt-recipe { padding: var(--space-2) 0; border-bottom: 1px solid var(--border); }
    .lt-recipe:last-child { border-bottom: none; }
    .lt-recipe-header { display: flex; align-items: baseline; gap: var(--space-2); flex-wrap: wrap; margin-bottom: 2px; }
    .lt-recipe-name { font-size: var(--font-size-sm); }
    .lt-recipe-meta { font-size: var(--font-size-xs); color: var(--text-muted); }
    .lt-recipe-lore { font-size: var(--font-size-xs); color: var(--text-italic); font-style: italic; margin: 2px 0 var(--space-1) 0; }
    .lt-recipe--locked { opacity: 0.55; }
    .lt-recipe--locked .lt-recipe-name { color: var(--text-muted); }
    .lt-recipe-lock { font-size: var(--font-size-xs); color: var(--text-muted); margin: 2px 0; }
    /* Ingredient / Slot Badges */
    .lt-badge-row { display: flex; flex-wrap: wrap; gap: 4px; margin: var(--space-1) 0; align-items: center; }
    .lt-badge { font-size: var(--font-size-xs); background: var(--bg-elevated); border: 1px solid var(--border);
                color: var(--text-muted); padding: 1px 6px; border-radius: var(--radius); white-space: nowrap; }
    /* Nur Textfarbe wechselt — Rahmen/Hintergrund bleiben unverändert (NT-UI-LABOR-BESTAND-01) */
    .lt-badge--available { color: var(--accent-green); }
    .lt-badge-sep { width: 1px; height: 12px; background: var(--border); margin: 0 2px; }
    .lt-recipe--illegal .lt-badge { border-color: color-mix(in srgb, var(--accent-red) 30%, var(--border)); }
    /* Shared */
    .lt-btn { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary);
               font-family: var(--font-mono); font-size: var(--font-size-xs); padding: 3px 10px;
               border-radius: var(--radius-btn); cursor: pointer; text-transform: uppercase; }
    .lt-btn:hover:not(:disabled) { border-color: var(--text-muted); }
    .lt-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .lt-btn--primary  { background: var(--accent-green); border-color: var(--accent-green); color: #000; }
    .lt-btn--danger   { border-color: var(--accent-red); color: var(--accent-red); }
    .lt-btn--legal    { border-color: var(--accent-gold); color: var(--accent-gold); background: transparent; }
    .lt-btn--legal:hover:not(:disabled) { background: color-mix(in srgb, var(--accent-gold) 10%, transparent); }
    .lt-btn--illegal  { border-color: var(--accent-red); color: var(--accent-red); background: transparent; }
    .lt-btn--illegal:hover:not(:disabled) { background: color-mix(in srgb, var(--accent-red) 10%, transparent); }
    /* Pulver-Wahl */
    .lt-choice-row { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap;
                     margin: var(--space-1) 0; padding: var(--space-1) var(--space-2);
                     background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius); }
    .lt-choice-label { font-size: var(--font-size-xs); color: var(--text-muted); text-transform: uppercase;
                       white-space: nowrap; }
    .lt-choice-select { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary);
                        font-family: var(--font-mono); font-size: var(--font-size-xs); padding: 2px 6px;
                        border-radius: var(--radius); flex: 1; min-width: 140px; }
    .lt-pulver-badge  { font-size: var(--font-size-xs); border: 1px solid; padding: 1px 6px;
                        border-radius: var(--radius); white-space: nowrap; font-family: var(--font-mono); }
    /* Verbrauchs-Zähler */
    .lt-charges-block { margin-bottom: var(--space-3); padding: var(--space-2); background: var(--bg-surface);
                        border: 1px solid var(--border); border-radius: var(--radius); }
    .lt-charges-row { display: flex; justify-content: space-between; align-items: baseline;
                      padding: 2px 0; font-size: var(--font-size-xs); }
    .lt-charges-row:first-child { margin-bottom: 2px; }
    .lt-charges-label { color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
    .lt-charges-val { font-weight: bold; }
    .lt-val-green  { color: var(--accent-green); }
    .lt-val-gold   { color: var(--accent-gold); }
    .lt-val-orange { color: var(--accent-orange); }
    .lt-val-red    { color: var(--accent-red); }
    .lt-val-muted  { color: var(--text-muted); }
    /* Zutaten-Bestand */
    .lt-stock-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: var(--space-2); }
    .lt-stock-row { display: flex; justify-content: space-between; align-items: baseline; gap: var(--space-2);
                    padding: 3px 8px; background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius); }
    .lt-stock-label { font-size: var(--font-size-xs); color: var(--text-muted); }
    .lt-stock-qty   { font-size: var(--font-size-sm); color: var(--text-primary); font-weight: bold; }
  `;
}

// ─── Render ───────────────────────────────────────────────────────────────────

function renderEquipmentSlots(): string {
  if (!labHideout) return '<p class="lt-empty">LADE EQUIPMENT...</p>';

  const laborSlots = labHideout.propertySlots
    .filter((s) => s.slotType === 'LABOR_WORKBENCH' && s.isUnlocked)
    .sort((a, b) => a.slotIndex - b.slotIndex);
  if (laborSlots.length === 0) return '<p class="lt-empty">Keine Labor-Slots verfügbar.</p>';

  const inventoryKeys = new Set(labHideout.inventory.map((i) => i.itemKey));
  const expLevel      = labHideout.expansionLevel;

  function renderSlot(slot: LabPropertySlot): string {
    return SLOT_FIELD_CONFIG.map((cfg) => {
      if (cfg.minLevel > expLevel) return '';

      const installed    = (slot as unknown as Record<string, unknown>)[cfg.field] as string | null;
      const isBroken     = cfg.field === 'glassItem' && slot.glassItemBroken && installed !== null;
      const availableItem = installed ? null : (cfg.validItems.find((v) => inventoryKeys.has(v)) ?? null);

      let statusHtml: string;
      let actionHtml: string;

      if (isBroken) {
        statusHtml = '<span class="lt-val-red">GEBROCHEN</span>';
        actionHtml = `<button class="lt-btn lt-btn--danger" data-lt-action="equip" data-lt-slot="${esc(slot.id)}" data-lt-field="${esc(cfg.field)}" data-lt-item="">AUSBAUEN</button>`;
      } else if (installed) {
        statusHtml = `<span class="lt-val-green">EINGEBAUT</span>`;
        actionHtml = `<button class="lt-btn lt-btn--danger" data-lt-action="equip" data-lt-slot="${esc(slot.id)}" data-lt-field="${esc(cfg.field)}" data-lt-item="">AUSBAUEN</button>`;
      } else {
        statusHtml = '<span class="lt-val-muted">LEER</span>';
        if (availableItem) {
          actionHtml = `<button class="lt-btn lt-btn--primary" data-lt-action="equip" data-lt-slot="${esc(slot.id)}" data-lt-field="${esc(cfg.field)}" data-lt-item="${esc(availableItem)}">INSTALLIEREN</button>`;
        } else {
          actionHtml = `<button class="lt-btn" disabled>INSTALLIEREN</button>`;
        }
      }

      return `
        <div class="lt-slot-row">
          <span class="lt-slot-name">${esc(cfg.label)}</span>
          <span>${statusHtml}</span>
          <span>${actionHtml}</span>
        </div>`;
    }).join('');
  }

  const firstSlot = laborSlots[0];
  if (laborSlots.length === 1 && firstSlot) {
    return `<div>${renderSlot(firstSlot)}</div>`;
  }

  return laborSlots.map((s, i) => `
    <div>
      <p class="lt-label" style="${i > 0 ? 'margin-top:var(--space-3);' : ''}color:var(--accent-gold);">SLOT ${String(s.slotIndex + 1)}</p>
      ${renderSlot(s)}
    </div>`).join('');
}

function renderCooks(): string {
  if (activeCooks.length === 0 && readyCooks.length === 0) {
    return '<p class="lt-empty">Keine aktiven Kochvorgänge.</p>';
  }

  const activeHtml = activeCooks.map((c) => {
    const completesAt = new Date(c.completesAt).getTime();
    const remaining   = Math.max(0, completesAt - Date.now());
    const startedAt   = new Date(c.startedAt).getTime();
    const total       = completesAt - startedAt;
    const pct         = total <= 0 ? 100 : Math.min(100, Math.max(0, Math.floor(((Date.now() - startedAt) / total) * 100)));
    return `
      <div class="lt-cook-row">
        <div class="lt-cook-meta">
          <span class="lt-cook-name">${esc(c.recipeLabel.toUpperCase())}</span>
          <span class="lt-cook-lab">${esc(c.propertyLabel)}</span>
          <span class="lt-cook-time" data-lt-countdown="${String(completesAt)}">${fmtCountdown(remaining)}</span>
        </div>
        <div class="lt-progress">
          <div class="lt-progress-fill" style="width:${String(pct)}%" data-lt-progress data-lt-started="${String(startedAt)}" data-lt-completes="${String(completesAt)}"></div>
        </div>
      </div>`;
  }).join('');

  const readyHtml = readyCooks.map((c) => `
    <div class="lt-cook-ready">
      <span class="lt-cook-name">${esc(c.recipeLabel.toUpperCase())}</span>
      <span class="lt-cook-lab">${esc(c.propertyLabel)}</span>
      <span class="lt-val-green">FERTIG</span>
      <button class="lt-btn lt-btn--primary" data-lt-action="collect" data-lt-cook-id="${esc(c.id)}">EINSAMMELN</button>
    </div>`).join('');

  const activePart = activeCooks.length > 0
    ? `<p class="lt-label" style="margin-top:var(--space-2)">LAUFEND (${String(activeCooks.length)})</p>${activeHtml}`
    : '';
  const readyPart = readyCooks.length > 0
    ? `<p class="lt-label" style="margin-top:var(--space-2)">FERTIG — EINSAMMELBAR (${String(readyCooks.length)})</p>${readyHtml}`
    : '';

  return activePart + readyPart;
}

function renderIngredientStock(): string {
  const owned = getOwnedIngredients();
  if (owned.length === 0) return '<p class="lt-empty">Keine Zutaten auf Lager.</p>';
  const rows = owned.map((o) => `
    <div class="lt-stock-row">
      <span class="lt-stock-label">${esc(ingredientLabel(o.key))}</span>
      <span class="lt-stock-qty">${String(o.qty)}</span>
    </div>`).join('');
  return `<div class="lt-stock-grid">${rows}</div>`;
}

function renderRecipeCard(r: RecipeCatalogEntry, availability: RecipeAvailability | undefined): string {
  const isLocked  = r.locked;
  const isIllegal = r.outputCurrency !== 'NOX';
  const choices   = r.ingredientChoices ?? [];

  // Verfügbarkeit kommt ausschließlich vom Backend (dieselbe Prüfung wie startCook) — bei
  // gesperrten Rezepten bewusst keine Einfärbung (konkurriert sonst mit dem GESPERRT-Hinweis).
  const ingredientBadges = Object.entries(r.ingredients)
    .map(([k, qty]) => {
      const available = !isLocked && (availability?.ingredients[k] ?? false);
      return `<span class="lt-badge${available ? ' lt-badge--available' : ''}">${esc(ingredientLabel(k))} ×${String(qty)}</span>`;
    })
    .join('');

  const slotBadges = r.requiredSlots
    .map((s) => {
      const label     = SLOT_KEY_LABEL[s] ?? s.toUpperCase();
      const available = !isLocked && (availability?.slots[s] ?? false);
      return `<span class="lt-badge${available ? ' lt-badge--available' : ''}">${esc(label)}</span>`;
    }).join('');

  // Pulver-Wahl-Selektoren (ingredientChoices)
  const choiceHtml = choices.map((choice, idx) => {
    const currentVal = pulverChoices.get(`${r.key}:${String(idx)}`) ?? choice.choiceGroup[0] ?? '';
    const options = choice.choiceGroup.map((k) =>
      `<option value="${esc(k)}"${k === currentVal ? ' selected' : ''}>${esc(ingredientLabel(k))}</option>`,
    ).join('');
    return `
      <div class="lt-choice-row">
        <span class="lt-choice-label">BENÖTIGT ×${String(choice.quantity)}</span>
        <select class="lt-choice-select" data-lt-choice-recipe="${esc(r.key)}" data-lt-choice-idx="${String(idx)}">${options}</select>
        ${pulverBadge(currentVal)}
      </div>`;
  }).join('');

  const hasChoices      = choices.length > 0;
  // Default (choiceGroup[0]) zählt als gültige Auswahl, auch wenn der Spieler
  // das Dropdown noch nicht manuell angefasst hat — sonst bleibt STARTEN grundlos disabled.
  const allChoicesMade  = choices.every((choice, idx) =>
    (pulverChoices.get(`${r.key}:${String(idx)}`) ?? choice.choiceGroup[0] ?? '') !== '',
  );
  const choiceReady     = !hasChoices || allChoicesMade;
  const canStart        = !isLocked && selectedPropId !== '' && choiceReady;
  const outputLine      = `${String(r.baseOutputQty)}× → LAGER`;
  const btnClass        = isIllegal ? 'lt-btn--illegal' : 'lt-btn--legal';

  const lockHint = isLocked
    ? `<p class="lt-recipe-lock">[GESPERRT] ${esc(r.lockReason ?? 'GESPERRT')}</p>`
    : '';

  const actionHtml = isLocked
    ? ''
    : `<button class="lt-btn ${btnClass}" ${canStart ? '' : 'disabled'} data-lt-action="start-cook" data-lt-recipe="${esc(r.key)}">STARTEN</button>`;

  return `
    <div class="lt-recipe${isLocked ? ' lt-recipe--locked' : ''}${isIllegal ? ' lt-recipe--illegal' : ''}">
      <div class="lt-recipe-header">
        <span class="lt-recipe-name">${esc(r.label.toUpperCase())}</span>
        <span class="lt-recipe-meta">${fmtMins(r.cookTimeMinutes)} · ${outputLine}</span>
        <span class="${RISK_CLASS[r.risk] ?? 'lt-val-muted'}">${esc(r.risk.replace('_', ' '))}</span>
      </div>
      <p class="lt-recipe-lore">${esc(r.lore)}</p>
      <div class="lt-badge-row">${ingredientBadges}${slotBadges ? `<span class="lt-badge-sep"></span>${slotBadges}` : ''}</div>
      ${choiceHtml}
      ${lockHint}
      <div style="margin-top:var(--space-1)">${actionHtml}</div>
    </div>`;
}

function renderCollapsibleSection(
  key:      'legal' | 'basis' | 'illegal',
  title:    string,
  count:    number,
  isOpen:   boolean,
  bodyHtml: string,
): string {
  const chevron   = isOpen ? '▼' : '▶';
  const colorCls  = `lt-rsec-title--${key}`;
  const dataAttr  = `data-lt-rsec="${key}"`;
  return `
    <div class="lt-rsec">
      <div class="lt-rsec-header" ${dataAttr}>
        <span class="lt-rsec-title ${colorCls}">${esc(title)}</span>
        <span class="lt-rsec-chevron">${chevron} ${String(count)}</span>
      </div>
      ${isOpen ? `<div class="lt-rsec-body">${bodyHtml}</div>` : ''}
    </div>`;
}

function renderRecipes(): string {
  if (recipes.length === 0) return '<p class="lt-empty">Kein Labor vorhanden.</p>';

  const availabilityForProp = recipeAvailability[selectedPropId] ?? {};
  const legal   = recipes.filter((r) => r.outputCurrency === 'NOX');
  const basis   = recipes.filter((r) => r.outputInventoryType === 'LABOR_INPUT');
  const illegal = recipes.filter((r) => r.outputCurrency !== 'NOX' && r.outputInventoryType !== 'LABOR_INPUT');

  const legalHtml   = legal.length > 0   ? legal.map((r) => renderRecipeCard(r, availabilityForProp[r.key])).join('')   : '<p class="lt-empty">Keine legalen Rezepte.</p>';
  const basisHtml   = basis.length > 0   ? basis.map((r) => renderRecipeCard(r, availabilityForProp[r.key])).join('')   : '<p class="lt-empty">Keine Basis-Produkte.</p>';
  const illegalHtml = illegal.length > 0 ? illegal.map((r) => renderRecipeCard(r, availabilityForProp[r.key])).join('') : '<p class="lt-empty">Keine illegalen Rezepte.</p>';

  return [
    renderCollapsibleSection('legal',   'LEGAL',           legal.length,   sectionLegalOpen,   legalHtml),
    renderCollapsibleSection('basis',   'BASIS PRODUKTE',  basis.length,   sectionBasisOpen,   basisHtml),
    renderCollapsibleSection('illegal', 'ILLEGAL',         illegal.length, sectionIllegalOpen, illegalHtml),
  ].join('');
}

function renderContent(): string {
  if (isLoading) return '<p class="lt-empty">LADE...</p>';
  if (loadError)  return `<p class="lt-empty lt-val-red">${esc(loadError)}</p>`;

  if (labHideouts.length === 0) {
    return renderHideoutSelectorEmpty('Kein Labor-Versteck vorhanden — im Tab IMMOBILIEN ausbauen.')
      + `<button class="lt-btn" style="margin-top:var(--space-3);color:var(--accent-gold);border-color:var(--accent-gold);" data-co-action="goto-verstecke">→ VERSTECK ZU LABOR UMBAUEN</button>`;
  }

  // Lab-Selektor — gemeinsame Komponente mit Gewächshaus/Rolltisch (gefiltert auf LABOR)
  const selectorHtml = renderHideoutSelectorTabs(
    labHideouts.map((h) => {
      const tier = labProps.find((p) => p.id === h.id)?.tier;
      const orgPrefix = h.orgBuilding ? 'ORG · ' : '';
      const label = orgPrefix + formatHideoutName(h).toUpperCase() + (tier ? ` [${tier}]` : '');
      return { key: h.id, label, isSelected: h.id === selectedPropId };
    }),
    'lt-htab',
  );

  const selectedProp  = labProps.find((p) => p.id === selectedPropId);
  const glovesCharges = selectedProp?.glovesCharges ?? 0;
  const stripsCharges = selectedProp?.stripsCharges ?? 0;
  const glovesColor   = glovesCharges === 0 ? 'lt-val-red' : glovesCharges <= 3 ? 'lt-val-orange' : 'lt-val-green';
  const stripsColor   = stripsCharges === 0 ? 'lt-val-red' : stripsCharges <= 5 ? 'lt-val-orange' : 'lt-val-green';
  // Ausgeschriebener Block zwischen Equipment und AKTIVE VORGÄNGE
  const chargesBlock = `
    <div class="lt-charges-block">
      <div class="lt-charges-row">
        <span class="lt-charges-label">Handschuhe</span>
        <span class="lt-charges-val ${glovesColor}">${String(glovesCharges)} Ladungen</span>
      </div>
      <div class="lt-charges-row">
        <span class="lt-charges-label">pH-Teststreifen</span>
        <span class="lt-charges-val ${stripsColor}">${String(stripsCharges)} Ladungen</span>
      </div>
    </div>`;

  const equipIcon = equipmentCollapsed ? '▶' : '▼';
  const equipBody = equipmentCollapsed ? '' : `<div style="margin-top:var(--space-2);">${renderEquipmentSlots()}</div>`;

  return `
    ${selectorHtml}
    <div class="lt-section">
      <div class="lt-equip-header">
        <span class="lt-equip-title">EQUIPMENT</span>
        <button class="lt-equip-toggle" data-lt-action="toggle-equipment">${equipIcon}</button>
      </div>
      ${equipBody}
    </div>
    ${chargesBlock}
    <div class="lt-section">
      <p class="lt-label">ZUTATEN-BESTAND</p>
      ${renderIngredientStock()}
    </div>
    <div class="lt-section">
      <p class="lt-label">AKTIVE VORGÄNGE</p>
      ${renderCooks()}
    </div>
    <div class="lt-section">
      <p class="lt-label">REZEPTE</p>
      ${renderRecipes()}
    </div>`;
}

function rerender(): void {
  const root = document.getElementById('lt-root');
  if (root) root.innerHTML = renderContent();
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadData(): Promise<void> {
  if (reloadPromise) return;
  reloadPromise = (async () => {
    try {
      const [hideoutsRes, labStatus, cookStatus] = await Promise.all([
        api.get<{ hideouts: LabHideout[] }>('/hideouts?includeOrg=1'),
        api.get<{
          labs: LaborPropertyInfo[];
          recipes: RecipeCatalogEntry[];
          recipeAvailability: Record<string, Record<string, RecipeAvailability>>;
        }>('/labor-v2/status?includeOrg=1'),
        api.get<{ activeCooks: CookBatchInfo[]; readyCooks: CookBatchInfo[] }>('/lab/cook/status'),
      ]);

      labProps           = labStatus.labs;
      recipes            = labStatus.recipes;
      recipeAvailability = labStatus.recipeAvailability;
      // Union aus Frontend-Filter (expansionType=LABOR) und Backend-Liste (deckt Legacy-Labore
      // mit isLabor=true ohne synchronisierten expansionType ab, siehe labor.service.ts).
      const laborIds = new Set(labProps.map((p) => p.id));
      labHideouts = filterHideoutsByExpansion(hideoutsRes.hideouts, 'LABOR')
        .concat(hideoutsRes.hideouts.filter((h) => laborIds.has(h.id) && h.expansionType !== 'LABOR'));

      if (!selectedPropId || !labHideouts.find((h) => h.id === selectedPropId)) {
        selectedPropId = labHideouts[0]?.id ?? '';
      }

      activeCooks = cookStatus.activeCooks;
      readyCooks  = cookStatus.readyCooks;

      labHideout = labHideouts.find((h) => h.id === selectedPropId) ?? null;

      isLoading = false;
      loadError = null;
    } catch (err) {
      loadError = err instanceof ApiError ? err.message : 'Ladefehler.';
      isLoading = false;
    } finally {
      reloadPromise = null;
    }
  })();
  await reloadPromise;
  rerender();
  startCountdownTimer();
}

async function loadHideoutData(): Promise<void> {
  if (!selectedPropId) return;
  try {
    const res = await api.get<{ hideouts: LabHideout[] }>('/hideouts?includeOrg=1');
    const laborIds = new Set(labProps.map((p) => p.id));
    labHideouts = filterHideoutsByExpansion(res.hideouts, 'LABOR')
      .concat(res.hideouts.filter((h) => laborIds.has(h.id) && h.expansionType !== 'LABOR'));
    labHideout  = labHideouts.find((h) => h.id === selectedPropId) ?? null;
  } catch {
    labHideout = null;
  }
}

// ─── Countdown ────────────────────────────────────────────────────────────────

function updateCountdowns(): void {
  const now = Date.now();
  document.querySelectorAll<HTMLElement>('[data-lt-countdown]').forEach((el) => {
    const ts = Number(el.dataset.ltCountdown ?? '0');
    el.textContent = fmtCountdown(ts - now);
  });
  document.querySelectorAll<HTMLElement>('[data-lt-progress]').forEach((el) => {
    const started   = Number(el.dataset.ltStarted   ?? '0');
    const completes = Number(el.dataset.ltCompletes ?? '0');
    const total     = completes - started;
    const pct       = total <= 0 ? 100 : Math.min(100, Math.max(0, Math.floor(((now - started) / total) * 100)));
    el.style.width  = `${String(pct)}%`;
  });

  if (activeCooks.some((c) => new Date(c.completesAt).getTime() <= now)) {
    void loadData();
  }
}

function startCountdownTimer(): void {
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(updateCountdowns, 1_000);
}

// ─── Aktions-Handler ──────────────────────────────────────────────────────────

async function handleEquip(slotId: string, field: string, value: string | null): Promise<void> {
  if (actionInFlight) return;
  actionInFlight = true;
  try {
    await api.patch<unknown>(`/hideouts/slots/${encodeURIComponent(slotId)}/equipment`, { field, itemKey: value });
    showToast(value ? 'Equipment installiert.' : 'Equipment ausgebaut.', 'info');
    await loadHideoutData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Equipment-Update fehlgeschlagen.', 'error');
    await loadHideoutData();
  } finally {
    actionInFlight = false;
    rerender();
  }
}

async function handleStartCook(recipeKey: string): Promise<void> {
  if (actionInFlight || !selectedPropId) return;
  actionInFlight = true;
  try {
    const recipe  = recipes.find((r) => r.key === recipeKey);
    const choices = recipe?.ingredientChoices ?? [];
    const chosenIngredients = choices.length > 0
      ? choices.map((_, idx) => pulverChoices.get(`${recipeKey}:${String(idx)}`) ?? choices[idx]?.choiceGroup[0] ?? '')
      : undefined;

    const body: Record<string, unknown> = { propertyId: selectedPropId, recipeKey };
    if (chosenIngredients !== undefined) body.chosenIngredients = chosenIngredients;

    const r = await api.post<{ batchId: string; completesAt: string }>('/lab/cook/start', body);
    showToast(`Kochvorgang gestartet — fertig um ${new Date(r.completesAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}.`, 'info');
    await loadData();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Start fehlgeschlagen.', 'error');
  } finally {
    actionInFlight = false;
  }
}

async function handleCollect(cookId: string): Promise<void> {
  if (actionInFlight) return;
  actionInFlight = true;
  try {
    const r = await api.post<{ outputQty: number; itemKey: string; glasswareBroke: boolean; protectionWorn: boolean }>(
      '/lab/cook/collect', { cookId },
    );
    let msg = `Eingesammelt! ${String(r.outputQty)}× ins Lager.`;
    if (r.glasswareBroke) msg += ' — GLASBRUCH — GLASKOLBEN-SET ZERSTÖRT';
    if (r.protectionWorn) msg += ' — Schutz abgenutzt.';
    showToast(msg, r.glasswareBroke ? 'error' : 'info');
    await loadData();
  } catch (err) {
    if (!tryOfferEmergencyGarage(err, () => { void loadData(); })) {
      showToast(err instanceof ApiError ? err.message : 'Einsammeln fehlgeschlagen.', 'error');
    }
  } finally {
    actionInFlight = false;
  }
}

// ─── Event-Listener ───────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('lt-root');
  if (!root) return;

  clickHandler = (e: Event) => {
    const target = e.target as HTMLElement;

    // Lab-Tab-Selektor
    const htab = target.closest<HTMLElement>('[data-lt-htab]');
    if (htab) {
      const propId = htab.dataset.ltHtab ?? '';
      if (propId && propId !== selectedPropId) {
        selectedPropId = propId;
        labHideout     = null;
        rerender();
        void loadHideoutData().then(() => { rerender(); });
      }
      return;
    }

    // Rezept-Sektionen aufklappen/zuklappen
    const rsec = target.closest<HTMLElement>('[data-lt-rsec]');
    if (rsec) {
      const key = rsec.dataset.ltRsec;
      if (key === 'legal')   { sectionLegalOpen   = !sectionLegalOpen;   rerender(); }
      if (key === 'basis')   { sectionBasisOpen   = !sectionBasisOpen;   rerender(); }
      if (key === 'illegal') { sectionIllegalOpen = !sectionIllegalOpen; rerender(); }
      return;
    }

    const btn = target.closest<HTMLElement>('[data-lt-action]');
    if (!btn || btn.hasAttribute('disabled')) return;

    const action = btn.dataset.ltAction ?? '';
    switch (action) {
      case 'toggle-equipment':
        equipmentCollapsed = !equipmentCollapsed;
        rerender();
        break;
      case 'equip':
        void handleEquip(
          btn.dataset.ltSlot  ?? '',
          btn.dataset.ltField ?? '',
          // Bewusst "||" statt "??": das AUSBAUEN-Markup setzt data-lt-item="" (leerer String,
          // nicht fehlend), der hier zusammen mit undefined zu null normalisiert werden muss.
          // Die Backend-Route validiert itemKey mit z.string().min(1).nullable()
          // (hideout.routes.ts) - ein durchgereichter leerer String würde die Zod-Validierung
          // hart ablehnen und den Ausbau-Button kaputt machen ("??" liesse "" unveraendert).
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- s.o.
          btn.dataset.ltItem || null,
        );
        break;
      case 'start-cook':
        void handleStartCook(btn.dataset.ltRecipe ?? '');
        break;
      case 'collect':
        void handleCollect(btn.dataset.ltCookId ?? '');
        break;
    }
  };

  changeHandler = (e: Event) => {
    const target = e.target as HTMLElement;
    const sel = target.closest<HTMLSelectElement>('[data-lt-choice-recipe]');
    if (sel) {
      const recipe = sel.dataset.ltChoiceRecipe ?? '';
      const idx    = sel.dataset.ltChoiceIdx    ?? '0';
      pulverChoices.set(`${recipe}:${idx}`, sel.value);
      rerender();
    }
  };

  root.addEventListener('click', clickHandler);
  root.addEventListener('change', changeHandler);
}

function detachListeners(): void {
  const root = document.getElementById('lt-root');
  if (!root) return;
  if (clickHandler)  { root.removeEventListener('click',  clickHandler);  clickHandler  = null; }
  if (changeHandler) { root.removeEventListener('change', changeHandler); changeHandler = null; }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function renderLaborTab(): string {
  addStyles();
  return `<div id="lt-root">${renderContent()}</div>`;
}

export function mountLaborTab(): void {
  if (isMounted) return;
  isMounted = true;
  isLoading = true;
  attachListeners();
  void loadData();
}

export async function refreshLaborTab(): Promise<void> {
  if (!isMounted) return;
  labHideouts        = [];
  labProps           = [];
  recipes            = [];
  recipeAvailability = {};
  labHideout         = null;
  activeCooks        = [];
  readyCooks         = [];
  await loadData();
}

export function cleanupLaborTab(): void {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  detachListeners();
  labHideouts        = [];
  labProps           = [];
  recipes            = [];
  recipeAvailability = {};
  selectedPropId     = '';
  labHideout         = null;
  activeCooks        = [];
  readyCooks         = [];
  equipmentCollapsed  = true;
  sectionLegalOpen    = false;
  sectionBasisOpen    = false;
  sectionIllegalOpen  = false;
  isLoading           = true;
  loadError           = null;
  reloadPromise       = null;
  actionInFlight      = false;
  isMounted           = false;
  pulverChoices.clear();
}
